import { CircleAlert, FlipHorizontal2, LoaderCircle, RotateCw, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { controller } from "../../app/controller.ts";
import type { SchematicEntry } from "../../lib/schematics.ts";
import { guessBlockColour } from "./blockColours.ts";
import { previewLod } from "./format.ts";
import { meshModel, SchematicStage, voxelModel, type StageModel } from "./stage.ts";

export type PreviewState = { kind: "idle" | "loading" | "ready"; lod: number } | { kind: "failed"; message: string };

/** colour of a block for the cube preview: the person's textures if any, else a guess from its name */
export const cubeColour = (name: string) => {
  const c = controller.blockColour(name);
  return c ? (c[0] << 16) | (c[1] << 8) | c[2] : guessBlockColour(name);
};

/** Build a stage model of a library schematic: real block models, or coloured cubes without textures. */
export async function loadModel(stage: SchematicStage, e: SchematicEntry, thumb = false): Promise<StageModel> {
  const lod = previewLod(e.blocks, thumb);
  if (stage.textured) {
    const r = await controller.schematicMesh(e.hash, lod);
    return meshModel(r, lod, stage.mats, e.size);
  }
  const v = await controller.schematicVoxels(e.hash, lod, thumb ? 60_000 : 160_000);
  return voxelModel(v, cubeColour, e.size);
}

/**
 * The selected schematic in 3D on a soft spotlight: orbit by dragging, zoom with the wheel, Turn R / Mirror F,
 * 3D / Top. Owns the canvas and the SchematicStage (handed to the window for the filmstrip thumbnails).
 */
export function StagePane({ entry, missing, turns, mirror, top, onTop, onTurn, onMirror, onStage, onRemove }: {
  entry: SchematicEntry | null;
  /** a schematic used in this world whose file isn't here */
  missing: { name: string; sourceUrl?: string } | null;
  turns: number;
  mirror: boolean;
  top: boolean;
  onTop: (top: boolean) => void;
  onTurn: () => void;
  onMirror: () => void;
  onStage: (s: SchematicStage | null) => void;
  onRemove: (hash: string) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<SchematicStage | null>(null);
  const [state, setState] = useState<PreviewState>({ kind: "idle", lod: 1 });
  const [touched, setTouched] = useState(false);

  // one renderer (with its own canvas: a canvas whose context was let go can't get a new one) while the view is open
  useEffect(() => {
    const el = document.createElement("canvas");
    el.className = "sv-canvas";
    canvas.current!.appendChild(el);
    let s: SchematicStage;
    try {
      s = new SchematicStage(el, controller.previewTextures());
    } catch (err) {
      el.remove();
      setState({ kind: "failed", message: `3D preview unavailable (${(err as Error).message})` });
      return;
    }
    s.onInteract = () => setTouched(true);
    // test hook (tools/e2e/phase-s.mjs): triangles drawn, models shown
    (el as HTMLCanvasElement & { __stage?: SchematicStage }).__stage = s;
    setStage(s);
    onStage(s);
    const ro = new ResizeObserver(() => { const r = box.current!.getBoundingClientRect(); s.resize(r.width, r.height); });
    ro.observe(box.current!);
    return () => { ro.disconnect(); onStage(null); s.dispose(); el.remove(); };
  }, []);

  // the selected file → its model (a newer selection wins; stale results are thrown away)
  const hash = entry?.hash ?? null;
  useEffect(() => {
    if (!stage) return;
    stage.setModel(null);
    if (!entry) { setState({ kind: "idle", lod: 1 }); return; }
    let alive = true;
    setState({ kind: "loading", lod: previewLod(entry.blocks) });
    setTouched(false);
    loadModel(stage, entry)
      .then((m) => {
        if (!alive) { m.dispose(); return; }
        stage.setModel(m);
        setState({ kind: "ready", lod: m.lod });
      })
      .catch((err: Error) => { if (alive) setState({ kind: "failed", message: err.message.replace(/^Error: /, "").split("\n")[0] }); });
    return () => { alive = false; };
  }, [stage, hash]);

  useEffect(() => { stage?.setOrientation(turns, mirror); }, [stage, turns, mirror]);
  useEffect(() => { stage?.setTop(top); }, [stage, top]);

  const dims = entry ? `${entry.size[0]} × ${entry.size[1]} × ${entry.size[2]}` : "";
  const showControls = !!entry && state.kind !== "failed";
  return (
    <div className="sv-stage" ref={box} data-state={state.kind}>
      <div ref={canvas} className="sv-canvas-box" role="img" aria-label={entry ? `${entry.name} in 3D` : "No preview"} hidden={!entry || state.kind === "failed"} />
      {entry && (
        <div className="sv-stage-top">
          <span className="sv-glass-pill sv-dims" title="Width × height × length">
            <span className="mono">{dims}</span><span className="sep">·</span><span>{entry.blocks.toLocaleString("en-US")} blocks</span>
          </span>
          {showControls && (
            <span className="sv-stage-tools">
              <span className="sv-glass-pill sv-view-seg" role="group" aria-label="View">
                <button type="button" aria-pressed={!top} onClick={() => onTop(false)}>3D</button>
                <button type="button" aria-pressed={top} onClick={() => onTop(true)}>Top</button>
              </span>
              <button type="button" className="btn sv-glass-btn" onClick={onTurn} title="Turn · R" aria-label="Turn · R"><RotateCw size={14} aria-hidden="true" />Turn<kbd>R</kbd></button>
              <button type="button" className="btn sv-glass-btn" aria-pressed={mirror} onClick={onMirror} title="Mirror · F" aria-label="Mirror · F"><FlipHorizontal2 size={14} aria-hidden="true" />Mirror<kbd>F</kbd></button>
            </span>
          )}
        </div>
      )}
      {state.kind === "loading" && (
        <div className="sv-stage-center sv-loading" role="status"><LoaderCircle className="spin" size={18} aria-hidden="true" /><span>Building the 3D preview…</span></div>
      )}
      {state.kind === "failed" && entry && (
        <div className="sv-stage-center sv-state" role="alert">
          <span className="sv-state-icon danger"><TriangleAlert size={20} aria-hidden="true" /></span>
          <b>Can't show this schematic</b>
          <span className="caption">{state.message}</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => onRemove(entry.hash)}>Remove from this computer</button>
        </div>
      )}
      {!entry && missing && (
        <div className="sv-stage-center sv-state">
          <span className="sv-state-icon amber"><CircleAlert size={20} aria-hidden="true" /></span>
          <b>{missing.name} isn't on this computer</b>
          <span className="caption">The file stays with the people who imported it. Get it from the original post, then import it here.</span>
        </div>
      )}
      {entry && state.kind === "ready" && state.lod > 1 && <span className="sv-lod chip" title="Very big builds are drawn with one cube per few blocks here; the map shows every block">Shown at lower detail · 1 cube = {state.lod} blocks</span>}
      {entry && state.kind === "ready" && <span className={`sv-hint${touched ? " quiet" : ""}`}>Drag to orbit · scroll to zoom</span>}
    </div>
  );
}
